import { useEffect, useRef, useState } from "react";
import { brandUrlNameError, normalizeBrandUrlName } from "@shared/brand-url-name";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type BrandUrlNameFieldProps = {
  brandId: string;
  urlName: string | null | undefined;
  onSaved: (brand: any) => void;
};

type AvailabilityResponse = {
  available: boolean;
  message?: string;
};

export function BrandUrlNameField({
  brandId,
  urlName,
  onSaved,
}: BrandUrlNameFieldProps) {
  const [value, setValue] = useState(() => normalizeBrandUrlName(urlName ?? ""));
  const [message, setMessage] = useState<string | null>(null);
  const [availability, setAvailability] = useState<"idle" | "checking" | "available" | "taken">("idle");
  const [isSaving, setIsSaving] = useState(false);
  const sequence = useRef(0);
  const saving = useRef(false);

  useEffect(() => {
    setValue(normalizeBrandUrlName(urlName ?? ""));
    setMessage(null);
    setAvailability("idle");
    sequence.current += 1;
  }, [brandId, urlName]);

  useEffect(() => () => {
    sequence.current += 1;
  }, []);

  const validate = (input: string) => brandUrlNameError(input);

  const checkAvailability = async (name: string): Promise<boolean> => {
    const currentSequence = ++sequence.current;
    const validationError = validate(name);
    if (validationError) {
      setAvailability("idle");
      setMessage(validationError);
      return false;
    }

    setAvailability("checking");
    setMessage(null);
    try {
      const response = await fetch(
        `/api/brands/${encodeURIComponent(brandId)}/url-name-availability?name=${encodeURIComponent(name)}`,
        { credentials: "same-origin" },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || "Could not check this URL name. Try again.");
      }
      const result = (await response.json()) as AvailabilityResponse;
      if (currentSequence !== sequence.current) return false;

      if (!result.available) {
        setAvailability("taken");
        setMessage(result.message || "This URL name is already in use.");
        return false;
      }
      setAvailability("available");
      setMessage(result.message || null);
      return true;
    } catch (error) {
      if (currentSequence !== sequence.current) return false;
      setAvailability("idle");
      setMessage(error instanceof Error ? error.message : "Could not check this URL name. Try again.");
      return false;
    }
  };

  const handleChange = (input: string) => {
    sequence.current += 1;
    setValue(normalizeBrandUrlName(input));
    setAvailability("idle");
    setMessage(null);
  };

  const handleSave = async () => {
    if (saving.current) return;
    const validationError = validate(value);
    if (validationError) {
      setAvailability("idle");
      setMessage(validationError);
      return;
    }

    saving.current = true;
    setIsSaving(true);
    setMessage(null);
    try {
      // Always recheck on save so the server remains authoritative.
      const isAvailable = await checkAvailability(value);
      if (!isAvailable) return;
      const response = await apiRequest("PATCH", `/api/brands/${encodeURIComponent(brandId)}/url-name`, {
        urlName: value,
      });
      const brand = await response.json();
      onSaved(brand);
    } catch (error) {
      setAvailability("idle");
      setMessage(error instanceof Error ? error.message : "Could not save this URL name. Try again.");
    } finally {
      saving.current = false;
      setIsSaving(false);
    }
  };

  const errorId = `brand-url-name-error-${brandId}`;
  const statusId = `brand-url-name-status-${brandId}`;

  return (
    <div className="space-y-2">
      <Label htmlFor={`brand-url-name-${brandId}`}>Brand URL name</Label>
      <p className="text-sm text-muted-foreground">
        Internal URL name only. This does not change the legacy slug or public URLs.
      </p>
      <div className="flex min-w-0 items-center gap-2">
        <span
          aria-hidden="true"
          className="shrink-0 text-sm text-muted-foreground"
        >
          /brands/
        </span>
        <Input
          id={`brand-url-name-${brandId}`}
          data-testid="input-brand-url-name"
          value={value}
          disabled={isSaving}
          onChange={(event) => handleChange(event.target.value)}
          onBlur={() => {
            if (value && !saving.current) void checkAvailability(value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void handleSave();
            }
          }}
          aria-invalid={!!message && availability !== "available"}
          aria-describedby={message ? errorId : statusId}
          autoComplete="off"
        />
        <Button
          type="button"
          variant="outline"
          data-testid="button-check-brand-url-name"
          onClick={() => void checkAvailability(value)}
          disabled={!value || availability === "checking" || isSaving}
        >
          {availability === "checking" ? "Checking..." : "Check"}
        </Button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div
          id={message ? errorId : statusId}
          aria-live="polite"
          className={`min-h-5 text-sm ${
            message && availability !== "available"
              ? "text-destructive"
              : availability === "available"
                ? "text-muted-foreground"
                : "text-muted-foreground"
          }`}
        >
          {message
            ? message
            : availability === "available"
              ? "This URL name is available."
              : ""}
        </div>
        <Button
          type="button"
          data-testid="button-save-brand-url-name"
          onClick={() => void handleSave()}
          disabled={!value || isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
}
